/**
 * Единственное место, где считаются деньги пула.
 *
 * ПОЧЕМУ НЕ `available_borrow` С ЦЕПИ. Три независимые причины:
 *
 * 1. Это ОДИН глобальный бюджет, показанный в трёх валютах. В живом логе
 *    2496.836 USDC / 2499.306 USDT / 1.326121 WETH — это одни и те же ~$2499.
 *    Контракт делит общий borrowableValue на цену запрошенного актива.
 *    Сложить их по активам — ошибиться втрое.
 *
 * 2. Это ДО-транзакционное число. Сеттлмент сначала кладёт входящую ногу в
 *    залог и только потом занимает исходящую, поэтому внутри сделки ёмкость
 *    равна headroom / (1 − maxLTV входящего): ×5 для стейблов (0.8),
 *    ×10 для WETH (0.9).
 *
 * 3. Оно игнорирует собственный залог, который выводится вообще без займа.
 *
 * Разрыв на снапшоте блока 25763327: витрина показала бы $248, фактический
 * потолок тикета USDC→USDT ≈ $16 025. Это 64×.
 *
 * ДЕНОМИНАЦИЯ ОРАКУЛА. getPrice(WETH) = 1e18, getPrice(USDC) ≈ 5.31e14 —
 * цены выражены в WETH при шкале 1e18, а НЕ в долларах. Чтобы получить
 * доллары, делим на цену стейбла, принятого за $1. Трактовка «/1e18 = доллары»
 * ошибается на стейблах примерно в 1884 раза.
 */
import type { Asset, AssetSnapshot, PoolDerived, PoolSnapshot } from '../types'
import { unitsToNumber } from './format'

const WAD = 1e18

/** Наш собственный колпак — он ниже контрактной стены и связывает первым. */
export const OWN_CAP_PCT = 79.5

const ASSETS: Asset[] = ['USDC', 'USDT', 'WETH']

/** Цена актива в долларах: oracle-units, приведённые к стейблу-эталону. */
function priceUsd(a: AssetSnapshot, usdRefPriceUnits: bigint): number {
  return Number(a.oraclePriceUnits) / Number(usdRefPriceUnits)
}

export function derivePool(s: PoolSnapshot): PoolDerived {
  const refPrice = s.assets[s.usdReferenceAsset].oraclePriceUnits

  let collateralUsd = 0
  let debtUsd = 0
  let weightedBorrowCeilingUsd = 0
  let venueAvailableBorrowUsd = 0
  let rateWeighted = 0

  const withdrawableUsd = {} as Record<Asset, number>
  const collateralUsdByAsset = {} as Record<Asset, number>

  for (const sym of ASSETS) {
    const a = s.assets[sym]
    const p = priceUsd(a, refPrice)

    const collat = unitsToNumber(a.lockedCollateralUnits, a.decimals) * p
    collateralUsdByAsset[sym] = collat
    const debt = unitsToNumber(a.debtUnits, a.decimals) * p
    const maxLtv = Number(a.maxLtvUnits) / WAD

    collateralUsd += collat
    debtUsd += debt
    weightedBorrowCeilingUsd += collat * maxLtv
    // Ставка витрины — средняя по долгу, взвешенная в долларах. Проверено на
    // их же скрине: (7689.73·15 + 561.70·6) / 8251.43 = 14.39 при показанных
    // 14.38.
    rateWeighted += debt * a.borrowRatePct

    // Своё, что можно вывести без займа: ограничено корзиной totalLockedDeposits.
    // Поле сегодня отсутствует в payload бота — тогда честно 0, а не догадка.
    const lockedBasket =
      a.totalLockedDepositsUnits == null
        ? 0
        : unitsToNumber(a.totalLockedDepositsUnits, a.decimals) * p
    withdrawableUsd[sym] = Math.min(collat, lockedBasket)

    // Витрина венью: один и тот же бюджет в валюте актива. Берём максимум,
    // а не сумму — именно потому, что это одно число, а не три.
    venueAvailableBorrowUsd = Math.max(
      venueAvailableBorrowUsd,
      unitsToNumber(a.availableBorrowUnits, a.decimals) * p,
    )
  }

  // Два разных запаса, и связывает меньший. Стена контракта — per-asset
  // maxLTV по активу-залогу; наш колпак — единая доля от всего залога и он
  // ниже, поэтому в норме упираемся именно в него.
  const headroomToWallUsd = Math.max(0, weightedBorrowCeilingUsd - debtUsd)
  const headroomToOwnCapUsd = Math.max(0, (collateralUsd * OWN_CAP_PCT) / 100 - debtUsd)
  const headroomUsd = Math.min(headroomToWallUsd, headroomToOwnCapUsd)
  const headroomBoundBy =
    headroomToOwnCapUsd <= headroomToWallUsd ? 'own_cap' : 'contract_wall'

  const ltvPct = collateralUsd > 0 ? (debtUsd / collateralUsd) * 100 : 0

  // Ёмкость внутри сделки: входящая нога сначала становится залогом.
  const inTradeCapacityUsd = {} as Record<Asset, number>
  for (const sym of ASSETS) {
    const maxLtv = Number(s.assets[sym].maxLtvUnits) / WAD
    inTradeCapacityUsd[sym] = headroomUsd / (1 - maxLtv)
  }

  // Стена контракта берётся из ФАКТИЧЕСКОЙ ориентации книги: после крупного
  // филла залог переезжает с USDT на USDC, а с WETH-залогом стена вообще 90%.
  // Сравнивать нужно в долларах, а не в сырых unit'ах: у WETH 18 знаков, у
  // стейблов 6, и пыль в 0.001 WETH (1e15 сырых единиц) перевешивала бы
  // $10k USDC (1e10 сырых единиц) при наивном сравнении bigint.
  const collateralAsset = ASSETS.reduce((best, sym) =>
    collateralUsdByAsset[sym] > collateralUsdByAsset[best] ? sym : best,
  )
  const contractWallPct = (Number(s.assets[collateralAsset].maxLtvUnits) / WAD) * 100

  return {
    collateralUsd,
    debtUsd,
    equityUsd: collateralUsd - debtUsd,
    headroomUsd,
    headroomToWallUsd,
    headroomToOwnCapUsd,
    headroomBoundBy,
    headroomPp: Math.max(0, OWN_CAP_PCT - ltvPct),
    inTradeCapacityUsd,
    withdrawableUsd,
    ltvPct,
    ownCapPct: OWN_CAP_PCT,
    contractWallPct,
    contractWallAsset: collateralAsset,
    liquidationPct: (Number(s.liquidationThresholdUnits) / WAD) * 100,
    usdRefAsset: s.usdReferenceAsset,
    venueAvailableBorrowUsd,
    blendedBorrowRatePct: debtUsd > 0 ? rateWeighted / debtUsd : 0,
    // Их «health factor» — это залог ÷ долг, а не расстояние до ликвидации.
    venueHealthFactor: debtUsd > 0 ? collateralUsd / debtUsd : Infinity,
  }
}

/**
 * Потолок тикета по направлению b→q, в долларах.
 * Все три члена конвертируются в USD ДО сравнения: на стейблах units ≈ $1 и
 * ошибка невидима, на WETH min(10.009, 12497) вместо min($18 855, $12 497)
 * промахивается в 1884 раза.
 */
export function maxTicketUsd(
  s: PoolSnapshot,
  d: PoolDerived,
  base: Asset,
  quote: Asset,
): number {
  const refPrice = s.assets[s.usdReferenceAsset].oraclePriceUnits
  const q = s.assets[quote]
  const poolLiquidityUsd = unitsToNumber(q.poolLiquidityUnits, q.decimals) * priceUsd(q, refPrice)

  const borrowLeg = Math.min(poolLiquidityUsd, d.inTradeCapacityUsd[base])
  const withdrawLeg = d.withdrawableUsd[quote]

  // Если залог с меньшим maxLTV, вывод стоит части разрешённого долга.
  const maxLtvBase = Number(s.assets[base].maxLtvUnits) / WAD
  const maxLtvQuote = Number(q.maxLtvUnits) / WAD
  const penalty = maxLtvBase < maxLtvQuote ? withdrawLeg * (maxLtvQuote - maxLtvBase) : 0

  return borrowLeg + Math.max(0, withdrawLeg - penalty)
}
